import { createElement, createHook, forwardRef } from "@ariakit/react-utils";
import type { Props } from "@ariakit/react-utils";
import type { ElementType } from "react";
import { withDefaultButtonType } from "../button/utils.ts";
import type { PopoverDismissOptions } from "../popover/popover-dismiss.tsx";
import { usePopoverDismiss } from "../popover/popover-dismiss.tsx";
import { useSelectScopedContext } from "./select-context.tsx";
import type { SelectStore } from "./select-store.ts";

const TagName = "button" satisfies ElementType;
type TagName = typeof TagName;

/**
 * Returns props to create a `SelectDismiss` component.
 * @deprecated Use `useComboboxDismiss` instead.
 * @see https://ariakit.com/components/select
 * @example
 * ```jsx
 * const props = useSelectDismiss();
 * <Role.button {...props} />
 * ```
 */
export const useSelectDismiss = createHook<TagName, SelectDismissOptions>(
  function useSelectDismiss({ store, ...props }) {
    const context = useSelectScopedContext();
    store = store || context;
    props = usePopoverDismiss({ store, ...props });
    return props;
  },
);

/**
 * Renders a button that hides a
 * [`SelectPopover`](https://ariakit.com/reference/select-popover) component
 * when clicked.
 *
 * When this component is rendered within
 * [`SelectPopover`](https://ariakit.com/reference/select-popover), all
 * [`SelectItem`](https://ariakit.com/reference/select-item) elements must be
 * rendered within a [`SelectList`](https://ariakit.com/reference/select-list)
 * instead of directly within the popover.
 * @deprecated Use
 * [`ComboboxDismiss`](https://ariakit.com/reference/combobox-dismiss) instead.
 * @see https://ariakit.com/components/select
 * @example
 * ```jsx {4}
 * <SelectProvider>
 *   <Select />
 *   <SelectPopover>
 *     <SelectDismiss />
 *     <SelectList>
 *       <SelectItem value="Apple" />
 *       <SelectItem value="Orange" />
 *     </SelectList>
 *   </SelectPopover>
 * </SelectProvider>
 * ```
 */
export const SelectDismiss = forwardRef(function SelectDismiss(
  props: SelectDismissProps,
) {
  const htmlProps = useSelectDismiss(withDefaultButtonType(props));
  return createElement(TagName, htmlProps);
});

/** @deprecated Use `ComboboxDismissOptions` instead. */
export interface SelectDismissOptions<
  T extends ElementType = TagName,
> extends PopoverDismissOptions<T> {
  /**
   * Object returned by the
   * [`useSelectStore`](https://ariakit.com/reference/use-select-store) hook.
   * If not provided, the closest
   * [`Select`](https://ariakit.com/reference/select) or
   * [`SelectProvider`](https://ariakit.com/reference/select-provider)
   * components' context will be used.
   */
  store?: SelectStore;
}

/** @deprecated Use `ComboboxDismissProps` instead. */
export type SelectDismissProps<T extends ElementType = TagName> = Props<
  T,
  SelectDismissOptions<T>
>;
